/**
 * OpportunityDetail - Slide-out panel for viewing and editing an opportunity.
 * Displays contact info, fields, activity timeline, quick stage move, and delete.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Edit3, Save, Trash2, User, Mail, Phone,
  DollarSign, Calendar, Tag, ArrowRight, Clock,
  FileText, MessageSquare, PhoneCall, Users,
  CheckSquare, Plus, AlertCircle, ChevronDown, Target
} from 'lucide-react';

const ACTIVITY_TYPES = [
  { value: 'NOTE', label: 'Note', icon: FileText },
  { value: 'EMAIL', label: 'Email', icon: Mail },
  { value: 'CALL', label: 'Call', icon: PhoneCall },
  { value: 'MEETING', label: 'Meeting', icon: Users },
  { value: 'TASK', label: 'Task', icon: CheckSquare }
];

const ACTIVITY_ICON_MAP = {
  NOTE: FileText,
  EMAIL: Mail,
  CALL: PhoneCall,
  MEETING: Users,
  TASK: CheckSquare,
  STAGE_CHANGE: ArrowRight
};

const SOURCE_OPTIONS = ['Website', 'Referral', 'Social Media', 'Cold Outreach', 'Event', 'Partner', 'Other'];

function OpportunityDetail({ opportunity, isOpen, onClose, apiWithAccessControl, onUpdate }) {
  const [detail, setDetail] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  // Activity form
  const [activityType, setActivityType] = useState('NOTE');
  const [activityDesc, setActivityDesc] = useState('');
  const [addingActivity, setAddingActivity] = useState(false);

  // Stage move
  const [movingStage, setMovingStage] = useState(false);

  // Delete
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Staff list for Assigned To
  const [staffList, setStaffList] = useState([]);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);

  // Auto-dismiss status
  useEffect(() => {
    if (statusMsg) {
      const timer = setTimeout(() => setStatusMsg(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [statusMsg]);

  // Load full detail
  const loadDetail = useCallback(async () => {
    if (!opportunity?.id) return;
    try {
      setLoading(true);
      const res = await apiWithAccessControl(`/admin/pipeline/opportunities/${opportunity.id}`);
      const data = await res.json();
      if (data.success !== false) {
        setDetail(data.opportunity || data);
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Failed to load details' });
        setDetail(opportunity); // Fallback to prop data
      }
    } catch (err) {
      console.error('Load detail error:', err);
      setDetail(opportunity);
    } finally {
      setLoading(false);
    }
  }, [opportunity, apiWithAccessControl]);

  const loadStages = useCallback(async () => {
    try {
      const res = await apiWithAccessControl('/admin/pipeline/stages');
      const data = await res.json();
      if (data.success !== false) {
        setStages(data.stages || []);
      }
    } catch (err) {
      console.error('Load stages error:', err);
    }
  }, [apiWithAccessControl]);

  const loadStaff = useCallback(async () => {
    try {
      const res = await apiWithAccessControl('/admin/rbac/staff');
      const data = await res.json();
      if (data.success !== false) {
        setStaffList(data.staff || []);
      }
    } catch (err) {
      console.error('Load staff error:', err);
    }
  }, [apiWithAccessControl]);

  useEffect(() => {
    if (isOpen && opportunity) {
      loadDetail();
      loadStages();
      loadStaff();
    }
  }, [isOpen, opportunity, loadDetail, loadStages, loadStaff]);

  // Start editing
  const startEdit = () => {
    setEditing(true);
    setEditForm({
      title: detail?.title || '',
      expectedValue: detail?.expectedValue || '',
      probability: detail?.probability || '',
      source: detail?.source || '',
      assignedTo: detail?.assignedTo || '',
      expectedCloseDate: detail?.expectedCloseDate ? detail.expectedCloseDate.split('T')[0] : '',
      notes: detail?.notes || '',
      tags: detail?.tags || ''
    });
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditForm({});
  };

  // Save edits
  const saveEdit = async () => {
    if (!detail?.id) return;
    try {
      setSaving(true);
      const res = await apiWithAccessControl(`/admin/pipeline/opportunities/${detail.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      const data = await res.json();
      if (data.success !== false) {
        setDetail(data.opportunity || { ...detail, ...editForm });
        setEditing(false);
        setStatusMsg({ type: 'success', text: 'Opportunity updated' });
        onUpdate && onUpdate();
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Failed to save' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to save changes' });
    } finally {
      setSaving(false);
    }
  };

  // Add Activity
  const submitActivity = async () => {
    if (!activityDesc.trim()) return;
    try {
      setAddingActivity(true);
      const res = await apiWithAccessControl(`/admin/pipeline/opportunities/${detail.id}/activities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: activityType, description: activityDesc.trim() })
      });
      const data = await res.json();
      if (data.success !== false) {
        setActivityDesc('');
        setStatusMsg({ type: 'success', text: 'Activity added' });
        loadDetail();
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Failed to add activity' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to add activity' });
    } finally {
      setAddingActivity(false);
    }
  };

  // Quick Stage Move
  const moveToStage = async (stageId) => {
    if (!detail?.id) return;
    try {
      setMovingStage(true);
      const res = await apiWithAccessControl(`/admin/pipeline/opportunities/${detail.id}/stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stageId })
      });
      const data = await res.json();
      if (data.success !== false) {
        setStatusMsg({ type: 'success', text: 'Stage updated' });
        loadDetail();
        onUpdate && onUpdate();
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Failed to move stage' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to move stage' });
    } finally {
      setMovingStage(false);
    }
  };

  // Delete
  const confirmDelete = async () => {
    if (!detail?.id) return;
    try {
      setDeleting(true);
      const res = await apiWithAccessControl(`/admin/pipeline/opportunities/${detail.id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success !== false) {
        onUpdate && onUpdate();
        onClose();
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'Failed to delete' });
      }
    } catch (err) {
      setStatusMsg({ type: 'error', text: 'Failed to delete opportunity' });
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Format helpers
  const formatCurrency = (val) => {
    if (!val && val !== 0) return '--';
    const num = Number(val);
    const naira = '₦' + num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    // Approximate USD conversion (rate can be made dynamic later)
    const usdRate = 1500;
    const usd = '$' + (num / usdRate).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return `${naira} (≈ ${usd})`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  const getActivityIcon = (type) => {
    const IconComp = ACTIVITY_ICON_MAP[type] || FileText;
    return <IconComp size={14} />;
  };

  const contactType = detail?.contactType || detail?.contact?.type;
  const contactName = detail?.contactName || detail?.contact?.name || '';
  const contactEmail = detail?.contactEmail || detail?.contact?.email || '';
  const contactPhone = detail?.contactPhone || detail?.contact?.phone || '';
  const activities = detail?.activities || [];
  const currentStageId = detail?.stageId || detail?.stage?.id;

  return (
    <>
      {/* Backdrop */}
      <div className="mkt-pipe-detail-backdrop" onClick={onClose} />

      {/* Panel */}
      <div className="mkt-pipe-detail-panel">
        {/* Header */}
        <div className="mkt-pipe-detail-header">
          <h2>{detail?.title || 'Opportunity'}</h2>
          {detail?.stageName && (
            <span className="mkt-pipe-stage-badge" style={{ marginRight: 8 }}>
              <span
                className="mkt-pipe-stage-badge-dot"
                style={{ backgroundColor: detail?.stageColor || '#94a3b8' }}
              />
              {detail.stageName}
            </span>
          )}
          <button className="mkt-pipe-btn-icon" onClick={onClose} title="Close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="mkt-pipe-detail-body">
          {loading ? (
            <div className="mkt-pipe-loading">
              <div className="mkt-pipe-spinner" />
              <span>Loading details...</span>
            </div>
          ) : (
            <>
              {/* Status Messages */}
              {statusMsg && (
                <div className={`mkt-pipe-status-msg ${statusMsg.type}`} style={{ margin: '0 0 16px' }}>
                  {statusMsg.type === 'success' ? <Target size={14} /> : <AlertCircle size={14} />}
                  {statusMsg.text}
                </div>
              )}

              {/* Edit Toggle */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, gap: 6 }}>
                {editing ? (
                  <>
                    <button className="mkt-pipe-btn-secondary mkt-pipe-btn-sm" onClick={cancelEdit}>
                      Cancel
                    </button>
                    <button
                      className="mkt-pipe-btn-primary mkt-pipe-btn-sm"
                      onClick={saveEdit}
                      disabled={saving}
                    >
                      <Save size={12} />
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : (
                  <button className="mkt-pipe-btn-secondary mkt-pipe-btn-sm" onClick={startEdit}>
                    <Edit3 size={12} />
                    Edit
                  </button>
                )}
              </div>

              {/* Contact Info */}
              <div className="mkt-pipe-detail-section">
                <h3>Contact</h3>
                <div className="mkt-pipe-detail-field">
                  <span className="mkt-pipe-detail-field-label">
                    <User size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />
                    Name
                  </span>
                  <span className="mkt-pipe-detail-field-value">{contactName || '--'}</span>
                </div>
                {contactEmail && (
                  <div className="mkt-pipe-detail-field">
                    <span className="mkt-pipe-detail-field-label">
                      <Mail size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />
                      Email
                    </span>
                    <span className="mkt-pipe-detail-field-value">{contactEmail}</span>
                  </div>
                )}
                {contactPhone && (
                  <div className="mkt-pipe-detail-field">
                    <span className="mkt-pipe-detail-field-label">
                      <Phone size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />
                      Phone
                    </span>
                    <span className="mkt-pipe-detail-field-value">{contactPhone}</span>
                  </div>
                )}
                {contactType && (
                  <div className="mkt-pipe-detail-field">
                    <span className="mkt-pipe-detail-field-label">Type</span>
                    <span className={`mkt-pipe-type-badge ${contactType === 'LEAD' ? 'lead' : 'waitlist'}`}>
                      {contactType}
                    </span>
                  </div>
                )}
              </div>

              {/* Fields */}
              <div className="mkt-pipe-detail-section">
                <h3>Details</h3>
                {editing ? (
                  <>
                    <div className="mkt-pipe-form-group">
                      <label className="mkt-pipe-form-label">Title</label>
                      <input
                        type="text"
                        className="mkt-pipe-form-input"
                        value={editForm.title || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      />
                    </div>
                    <div className="mkt-pipe-form-row">
                      <div className="mkt-pipe-form-group">
                        <label className="mkt-pipe-form-label">Expected Value (₦)</label>
                        <input
                          type="number"
                          className="mkt-pipe-form-input"
                          value={editForm.expectedValue || ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, expectedValue: e.target.value }))}
                        />
                      </div>
                      <div className="mkt-pipe-form-group">
                        <label className="mkt-pipe-form-label">Probability (%)</label>
                        <input
                          type="number"
                          className="mkt-pipe-form-input"
                          min="0"
                          max="100"
                          value={editForm.probability || ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, probability: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="mkt-pipe-form-row">
                      <div className="mkt-pipe-form-group">
                        <label className="mkt-pipe-form-label">Source</label>
                        <select
                          className="mkt-pipe-form-select"
                          value={editForm.source || ''}
                          onChange={(e) => setEditForm(prev => ({ ...prev, source: e.target.value }))}
                        >
                          <option value="">Select source</option>
                          {SOURCE_OPTIONS.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div className="mkt-pipe-form-group">
                        <label className="mkt-pipe-form-label">Assigned To</label>
                        <select
                          className="mkt-pipe-form-select"
                          value={staffList.some(s => s.email === editForm.assignedTo) || editForm.assignedTo === '' ? editForm.assignedTo : '__custom__'}
                          onChange={(e) => {
                            if (e.target.value === '__custom__') {
                              setShowAssignDropdown(true);
                              setEditForm(prev => ({ ...prev, assignedTo: '' }));
                            } else {
                              setShowAssignDropdown(false);
                              setEditForm(prev => ({ ...prev, assignedTo: e.target.value }));
                            }
                          }}
                        >
                          <option value="">-- Select --</option>
                          {staffList.map(s => (
                            <option key={s._id} value={s.email}>{s.email} ({s.staffRole})</option>
                          ))}
                          <option value="__custom__">Custom name...</option>
                        </select>
                        {showAssignDropdown && (
                          <input
                            type="text"
                            className="mkt-pipe-form-input"
                            style={{ marginTop: 6 }}
                            placeholder="Enter custom name..."
                            value={editForm.assignedTo || ''}
                            onChange={(e) => setEditForm(prev => ({ ...prev, assignedTo: e.target.value }))}
                            autoFocus
                          />
                        )}
                      </div>
                    </div>
                    <div className="mkt-pipe-form-group">
                      <label className="mkt-pipe-form-label">Expected Close Date</label>
                      <input
                        type="date"
                        className="mkt-pipe-form-input"
                        value={editForm.expectedCloseDate || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, expectedCloseDate: e.target.value }))}
                      />
                    </div>
                    <div className="mkt-pipe-form-group">
                      <label className="mkt-pipe-form-label">Notes</label>
                      <textarea
                        className="mkt-pipe-form-textarea"
                        value={editForm.notes || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mkt-pipe-detail-field">
                      <span className="mkt-pipe-detail-field-label">Expected Value</span>
                      <span className="mkt-pipe-detail-field-value">
                        {formatCurrency(detail?.expectedValue)}
                      </span>
                    </div>
                    <div className="mkt-pipe-detail-field">
                      <span className="mkt-pipe-detail-field-label">Probability</span>
                      <span className="mkt-pipe-detail-field-value">
                        {detail?.probability != null ? `${detail.probability}%` : '--'}
                      </span>
                    </div>
                    <div className="mkt-pipe-detail-field">
                      <span className="mkt-pipe-detail-field-label">Source</span>
                      <span className="mkt-pipe-detail-field-value">{detail?.source || '--'}</span>
                    </div>
                    <div className="mkt-pipe-detail-field">
                      <span className="mkt-pipe-detail-field-label">Assigned To</span>
                      <span className="mkt-pipe-detail-field-value">{detail?.assignedTo || '--'}</span>
                    </div>
                    <div className="mkt-pipe-detail-field">
                      <span className="mkt-pipe-detail-field-label">Expected Close</span>
                      <span className="mkt-pipe-detail-field-value">
                        {formatDate(detail?.expectedCloseDate)}
                      </span>
                    </div>
                    <div className="mkt-pipe-detail-field">
                      <span className="mkt-pipe-detail-field-label">Created</span>
                      <span className="mkt-pipe-detail-field-value">
                        {formatDate(detail?.createdAt)}
                      </span>
                    </div>
                    {detail?.tags && (
                      <div className="mkt-pipe-detail-field">
                        <span className="mkt-pipe-detail-field-label">Tags</span>
                        <span className="mkt-pipe-detail-field-value">{detail.tags}</span>
                      </div>
                    )}
                    {detail?.notes && (
                      <div style={{ marginTop: 8, fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
                        {detail.notes}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Quick Stage Move */}
              <div className="mkt-pipe-detail-section">
                <h3>Move to Stage</h3>
                <div className="mkt-pipe-quick-stages">
                  {stages.map(stage => (
                    <button
                      key={stage.id}
                      className={`mkt-pipe-quick-stage-btn ${String(currentStageId) === String(stage.id) ? 'active' : ''}`}
                      onClick={() => moveToStage(stage.id)}
                      disabled={movingStage || String(currentStageId) === String(stage.id)}
                    >
                      <span
                        className="mkt-pipe-stage-dot"
                        style={{ width: 7, height: 7, backgroundColor: stage.color || '#94a3b8', borderRadius: '50%', display: 'inline-block' }}
                      />
                      {stage.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Add Activity */}
              <div className="mkt-pipe-detail-section">
                <h3>Add Activity</h3>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <select
                    className="mkt-pipe-form-select"
                    style={{ width: 'auto', flex: '0 0 auto' }}
                    value={activityType}
                    onChange={(e) => setActivityType(e.target.value)}
                  >
                    {ACTIVITY_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  className="mkt-pipe-form-textarea"
                  placeholder="Describe the activity..."
                  value={activityDesc}
                  onChange={(e) => setActivityDesc(e.target.value)}
                  rows={2}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                  <button
                    className="mkt-pipe-btn-primary mkt-pipe-btn-sm"
                    onClick={submitActivity}
                    disabled={addingActivity || !activityDesc.trim()}
                  >
                    <Plus size={12} />
                    {addingActivity ? 'Adding...' : 'Add Activity'}
                  </button>
                </div>
              </div>

              {/* Activity Timeline */}
              {activities.length > 0 && (
                <div className="mkt-pipe-detail-section">
                  <h3>Activity Timeline</h3>
                  <div className="mkt-pipe-timeline">
                    {activities.map((activity, idx) => (
                      <div key={activity.id || idx} className="mkt-pipe-timeline-item">
                        <div className="mkt-pipe-timeline-icon">
                          {getActivityIcon(activity.type)}
                        </div>
                        <div className="mkt-pipe-timeline-content">
                          <div className="mkt-pipe-timeline-desc">{activity.description}</div>
                          <div className="mkt-pipe-timeline-time">{formatTime(activity.createdAt)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Delete */}
              <div className="mkt-pipe-detail-section" style={{ borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
                {!showDeleteConfirm ? (
                  <button
                    className="mkt-pipe-btn-danger"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 size={14} />
                    Delete Opportunity
                  </button>
                ) : (
                  <div className="mkt-pipe-confirm-delete">
                    <span>Are you sure you want to delete this opportunity? This cannot be undone.</span>
                    <button
                      className="mkt-pipe-btn-danger mkt-pipe-btn-sm"
                      onClick={confirmDelete}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting...' : 'Confirm'}
                    </button>
                    <button
                      className="mkt-pipe-btn-secondary mkt-pipe-btn-sm"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default OpportunityDetail;
